import { Box, Container, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../utils/i18n';

/**
 * Full-viewport hero section — the first thing visitors see.
 * Features gradient headline text, responsive font scaling,
 * animated background gradient, and dual CTA buttons.
 */
export default function HeroSection() {
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        // Animated radial gradient background
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background:
            'radial-gradient(circle at 30% 40%, rgba(108,99,255,0.15) 0%, transparent 50%), ' +
            'radial-gradient(circle at 70% 60%, rgba(0,217,166,0.10) 0%, transparent 50%), ' +
            'radial-gradient(circle at 50% 50%, rgba(108,99,255,0.05) 0%, transparent 70%)',
          animation: 'heroGradientDrift 12s ease-in-out infinite alternate',
          zIndex: 0,
        },
        '@keyframes heroGradientDrift': {
          '0%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(-2%, 3%) scale(1.05)' },
          '100%': { transform: 'translate(2%, -2%) scale(1)' },
        },
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          py: { xs: 8, md: 0 },
        }}
      >
        {/* Gradient headline */}
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
            lineHeight: 1.2,
            mb: 2,
            background: 'linear-gradient(135deg, #6C63FF 0%, #00D9A6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t('landing.hero.title')}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="h6"
          component="p"
          sx={{
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto',
            mb: 5,
            fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
            lineHeight: 1.6,
          }}
        >
          {t('landing.hero.subtitle')}
        </Typography>

        {/* CTA buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/dashboard')}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6C63FF, #00D9A6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a52e0, #00c494)',
              },
            }}
          >
            {t('landing.hero.getStarted')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/login')}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              borderRadius: 2,
              borderColor: 'rgba(108,99,255,0.5)',
              color: '#6C63FF',
              '&:hover': {
                borderColor: '#6C63FF',
                backgroundColor: 'rgba(108,99,255,0.08)',
              },
            }}
          >
            {t('landing.hero.login')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
